// src/views/Client/ClientsDashboard.js
import React, { useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import {
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { getRemaining } from '../../utils/invoices';
import { format } from 'date-fns';
import { getClientConversation } from '../../api/backend';

const ClientsDashboard = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [balancePendiente, setBalancePendiente] = useState(0);
  const [proyectoActivo, setProyectoActivo] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return;

      try {
        // 1. Perfil
        const profileRef = query(collection(db, 'clients'), where('email', '==', user.email));
        const profileSnap = await getDocs(profileRef);

        if (!profileSnap.empty) {
          const profile = { id: profileSnap.docs[0].id, ...profileSnap.docs[0].data() };
          setUserProfile(profile);

          // 2. Invoices
          const invoicesRef = collection(db, 'clients', profile.id, 'invoices');
          const invoiceSnap = await getDocs(invoicesRef);
          const invoices = invoiceSnap.docs.map(doc => doc.data());

          const totalPendiente = invoices
            .filter(inv => inv.status !== 'paid')
            .reduce((acc, inv) => acc + Number(getRemaining(inv)), 0);
          setBalancePendiente(totalPendiente);

          // 3. Proyecto activo
          const projectsRef = collection(db, 'clients', profile.id, 'projects');
          const projectsSnap = await getDocs(projectsRef);
          const allProjects = projectsSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          }));

          const activos = allProjects.filter(p => p.estado === 'activo');
          setProyectoActivo(activos[0] || null);
        }

        // 4. Mensajes no leídos
        const conversation = await getClientConversation();
        setHasUnreadMessages((conversation.unreadCount || 0) > 0);
      } catch (err) {
        console.error('❌ Error al cargar dashboard:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const refreshUnread = async () => {
      try {
        const conversation = await getClientConversation();
        if (!cancelled) {
          setHasUnreadMessages((conversation.unreadCount || 0) > 0);
        }
      } catch (error) {
        console.error('❌ Error actualizando mensajes no leídos:', error);
      }
    };

    const intervalId = window.setInterval(refreshUnread, 10000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  if (loading) return <p className="p-4 text-gray-500">Cargando tu panel...</p>;
  if (!userProfile) return <p className="p-4 text-red-500">No se encontró tu perfil.</p>;

  const timeline = (proyectoActivo?.projectTimeline || []).sort(
    (a, b) => b.timestamp?.seconds - a.timestamp?.seconds
  );

  const phases = proyectoActivo?.projectPhases || [];

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-semibold text-gray-800">Bienvenido, {userProfile.name}</h1>

      {/* 🔔 Notificaciones */}
      {hasUnreadMessages && (
        <div className="bg-blue-100 border-l-4 border-blue-500 p-4 rounded shadow-sm">
          <p className="text-blue-800 font-medium">📩 Tienes mensajes nuevos del administrador.</p>
          <button
            onClick={() => navigate('/clients/messages')}
            className="mt-2 inline-block px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Ver mensajes
          </button>
        </div>
      )}

      {/* 💰 Balance y proyecto */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Balance */}
        <div className="bg-white p-4 shadow rounded">
          <h2 className="font-semibold text-gray-700 mb-2">Balance Pendiente</h2>
          <p className="text-2xl font-bold text-red-600">
            ${balancePendiente.toFixed(2)}
          </p>
          <button
            onClick={() => navigate('/clients/invoices')}
            className="mt-2 inline-block text-sm text-blue-600 hover:underline"
          >
            Ver facturas →
          </button>
        </div>

        {/* Proyecto activo */}
        <div className="bg-white p-4 shadow rounded">
          <h2 className="font-semibold text-gray-700 mb-2">Proyecto Activo</h2>
          {proyectoActivo ? (
            <>
              <p className="text-lg font-medium text-gray-800">
                {proyectoActivo.projectName}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                Estado: {proyectoActivo.estado}
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-500">No tienes proyectos activos aún.</p>
          )}
        </div>
      </div>

      {/* 🕒 Línea de tiempo */}
      {proyectoActivo && (
        <div className="bg-white p-4 shadow rounded space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">🕒 Línea de Tiempo</h3>
          {timeline.length === 0 ? (
            <p className="text-sm text-gray-500">Aún no hay eventos registrados.</p>
          ) : (
            timeline.map((event, idx) => (
              <div key={idx} className="border-l-2 pl-4 relative">
                <div
                  className={`w-3 h-3 rounded-full absolute -left-1 top-1 ${
                    event.color === 'blue'
                      ? 'bg-blue-500'
                      : event.color === 'green'
                      ? 'bg-green-500'
                      : event.color === 'yellow'
                      ? 'bg-yellow-500'
                      : 'bg-gray-400'
                  }`}
                ></div>
                <h4 className="text-md font-semibold text-gray-800">{event.title}</h4>
                <p className="text-sm text-gray-600">{event.description}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {format(event.timestamp.toDate(), 'PPPppp')}
                </p>
              </div>
            ))
          )}
        </div>
      )}

      {/* 🧩 Fases del proyecto */}
      {proyectoActivo && (
        <div className="bg-white p-4 shadow rounded space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">🧩 Fases del Proyecto</h3>
          {phases.length === 0 ? (
            <p className="text-sm text-gray-500">No hay fases asignadas a este proyecto aún.</p>
          ) : (
            phases.map((phase, idx) => (
              <div
                key={idx}
                className={`p-3 rounded border-l-4 shadow-sm ${
                  phase.status === 'completado'
                    ? 'border-green-500 bg-green-50'
                    : phase.status === 'en_proceso'
                    ? 'border-yellow-500 bg-yellow-50'
                    : 'border-gray-300 bg-gray-100'
                }`}
              >
                <h4 className="text-md font-semibold text-gray-800">{phase.name}</h4>
                <p className="text-sm text-gray-600">Estado: {phase.status}</p>
                {phase.notes && (
                  <p className="text-sm text-gray-600">📝 {phase.notes}</p>
                )}
                {phase.startDate && (
                  <p className="text-sm text-gray-500">
                    📅 Inicio: {new Date(phase.startDate.seconds * 1000).toLocaleDateString()}
                  </p>
                )}
                {phase.deadline && (
                  <p className="text-sm text-gray-500">
                    ⏰ Deadline: {new Date(phase.deadline.seconds * 1000).toLocaleDateString()}
                  </p>
                )}
                {phase.requiresPayment && (
                  <p className="text-sm text-red-500 font-medium">💸 Esta fase requiere pago</p>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ClientsDashboard;
